import React, { useState, useEffect, ComponentType, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import {
  addWebhookSubscriptionStart,
  updateWebhookSubscriptionStart,
  deleteWebhookSubscriptionStart
} from 'redux/webhook';
import { withFormik } from 'formik';
import {
  CFCProps,
  WebhookSubscriptionFormValues,
  WebhookSubscriptionEventItem,
  WebhookSubscriptionFormProps,
  WebhookSubscriptionResponse
} from 'types';
import Form from './form';
import DeleteWarning from './deleteWarning';
import * as Yup from 'yup';

const Schema = Yup.object().shape({
  url: Yup.string()
    .required('Web service URL is required')
    .url('Web service URL is not valid')
    .matches(/^https/, 'Web service URL is not valid')
    .max(512, 'Web service URL cannot be more than 512 characters'),
  clientKey: Yup.string()
    .required('Signing secret key is required')
    .min(8, 'Signing secret key is min 8 character and max 32 character length')
    .max(
      32,
      'Signing secret key is min 8 character and max 32 character length'
    )
});

const EventFormModal: React.FC<Props> = ({
  submitting,
  subscription,
  event,
  handleCancel,
  error
}) => {
  const initialApp = useRef(false);

  const dispatch: any = useDispatch();
  const [deletingEventId, setDeletingEventId] = useState<number | undefined>();
  const [apiCalled, setApiCalled] = useState(false);
  const [formValues, setFormValues] = useState<WebhookSubscriptionFormValues>({
    url: '',
    clientKey: '',
    isPaused: false
  });

  const confirmDelete = (id: number | undefined) => {
    if (id) {
      dispatch(deleteWebhookSubscriptionStart(id));
    }
  };

  const handleDelete = (id: number | undefined) => {
    setDeletingEventId(id);
  };

  useEffect(() => {
    if (subscription) {
      setFormValues({
        url: subscription.url,
        clientKey: subscription.clientKey,
        customHeaders: Array.isArray(subscription.customHeaders)
          ? subscription.customHeaders
          : undefined,
        isPaused: subscription.isPaused
      });
    }
  }, [subscription]);

  useEffect(() => {
    if (!initialApp.current) {
      initialApp.current = true;
      return;
    }

    if (!submitting) {
      if (error) {
        setApiCalled(false);
      } else {
        setTimeout(() => {
          setApiCalled(false);
          handleCancel();
        }, 1000);
      }
    } else {
      setApiCalled(true);
    }
  }, [error, submitting, handleCancel]);

  const EventFormFormikComponent: ComponentType<any> = withFormik<
    WebhookSubscriptionFormProps,
    WebhookSubscriptionFormValues
  >({
    mapPropsToValues: () => formValues,
    validationSchema: Schema,
    handleSubmit: (
      values: WebhookSubscriptionFormValues,
      { setSubmitting }
    ) => {
      setSubmitting(true);
      if (subscription) {
        dispatch(
          updateWebhookSubscriptionStart({
            subscriptionId: subscription.id,
            data: {
              clientKey: values.clientKey,
              customHeaders: values?.customHeaders,
              isPaused: values.isPaused
            }
          })
        );
      } else {
        dispatch(
          addWebhookSubscriptionStart({
            webhook: event?.type || '',
            url: values.url,
            clientKey: values.clientKey,
            customHeaders: values?.customHeaders,
            isPaused: values.isPaused,
            organisationId: event?.organisationId
          })
        );
      }
    }
  })(Form);

  return (
    <div className={'webhook-subscriptions-event-modal-container'}>
      <section className="webhook-subscriptions-event-modal">
        <EventFormFormikComponent
          submitting={submitting || false}
          deleting={deletingEventId !== undefined}
          isUpdate={subscription !== undefined}
          disableDeleted={!subscription?.id}
          eventLabel={event?.label || ''}
          handleDelete={() => handleDelete(subscription?.id)}
          handleCancel={handleCancel}
          apiCalled={apiCalled}
        />
      </section>
      {deletingEventId && !submitting && !apiCalled && (
        <DeleteWarning
          onClose={() => setDeletingEventId(undefined)}
          onConfirm={() => confirmDelete(deletingEventId)}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ webhookCache }: RootState) => ({
  submitting: webhookCache?.subscriptions?.submitting,
  error: webhookCache?.subscriptions?.error
});

const connector = connect(mapStateToProps);

type OwnProps = {
  subscription?: WebhookSubscriptionResponse;
  event?: WebhookSubscriptionEventItem;
  handleCancel: Function;
};

type Props = CFCProps<typeof connector> & OwnProps;

EventFormModal.displayName = 'EventFormModal';
export default React.memo(connector(EventFormModal));
